import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Loads the next page when this sentinel scrolls into view.
 * Place it as the last child of the scrolling container so that container is the observer root.
 */
export function InfiniteScroll({
  hasMore,
  loading,
  onLoad,
  className,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoad: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || loading) return;
    const root = el.parentElement;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onLoadRef.current();
      },
      { root, rootMargin: "160px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  if (!hasMore && !loading) return null;
  return (
    <div ref={ref} className={cn("flex justify-center py-3", className)}>
      {loading ? <Loader2 className="size-4 animate-spin text-muted-foreground" /> : <span className="h-4" />}
    </div>
  );
}
